/**
 * Asking Bear from a comment thread. The thread is where the conversation
 * about that spot lives: the comment and Bear's answer both stay in it, and
 * the chat gets one line that points back. A view that hosts an agent
 * conversation provides where an ask goes; a view that doesn't (history, a
 * closed task) provides nothing, and its composers don't offer Bear.
 */
import type { DataUIPart, FileUIPart, UIMessage } from "ai";
import type { CommentAskPart } from "apprentices/dataParts/commentAsk";
import type { StudioDataUIParts } from "apprentices/dataParts/types";
import { DEFAULT_DRAFTER_MODEL_ID } from "apprentices/worker/models";
import { chatCommands, mintId } from "den/client/use-chat-feed";
import { createContext, useCallback, useContext } from "react";
import { invariant } from "ts-invariant";

import { createChat, useSharedProjectChats } from "@/panes/use-project-chats";
import { workspaceContextParts } from "@/panes/workspace-context-parts";

/** An ask, with a picture (a data URL) when the thread is on a drawing. */
export type AskBear = (ask: CommentAskPart, picture?: string) => Promise<void>;

const AskBearContext = createContext<AskBear | null>(null);

export const AskBearProvider = AskBearContext.Provider;

/** Where this view sends a comment that asks Bear, or null when it can't. */
export function useAskBear(): AskBear | null {
  return useContext(AskBearContext);
}

/** The ask as a chat message: hidden context for the model, the part the
 *  chat draws as one line, and the picture beside them as an attachment. */
export function commentAskMessage(
  ask: CommentAskPart,
  picture?: string,
): UIMessage {
  const part: DataUIPart<StudioDataUIParts> = {
    type: "data-commentAsk",
    id: ask.threadId,
    data: ask,
  };
  return {
    id: mintId(),
    role: "user",
    parts: [
      ...workspaceContextParts(new Date(), [part]),
      part,
      ...(picture ? [pictureFile(picture)] : []),
    ],
  };
}

function pictureFile(dataUrl: string): FileUIPart {
  const mediaType = /^data:([^;,]+)[;,]/.exec(dataUrl)?.[1];
  invariant(mediaType, "A picture must be a data URL");
  return { type: "file", mediaType, url: dataUrl };
}

export function commentAskOf(message: UIMessage): CommentAskPart | undefined {
  const part = message.parts.find((p) => p.type === "data-commentAsk");
  return part && "data" in part ? (part.data as CommentAskPart) : undefined;
}

/**
 * Send asks to the conversation this view shows when it works in this
 * view's scope, or else to a new one there, which `open` then puts on screen.
 */
export function useAskBearDispatch({
  projectId,
  taskId,
  chatId,
  open,
}: {
  projectId: string;
  /** The branch the view shows: its comments are that branch's copy. */
  taskId: string | undefined;
  /** The conversation on screen, if any. */
  chatId: string | null;
  open: (chatId: string) => void;
}): AskBear {
  const chats = useSharedProjectChats();
  const { list, adopt } = chats;
  return useCallback(
    async (ask, picture) => {
      const onScreen = list.find(
        (chat) => chat.id === chatId && chat.task_id === (taskId ?? null),
      );
      const chat =
        onScreen ??
        (await createChat(projectId, DEFAULT_DRAFTER_MODEL_ID, taskId));
      await chatCommands(`${projectId}:${chat.id}`, {
        agentClass: "drafter-host",
      }).sendMessage(mintId(), commentAskMessage(ask, picture));
      if (!onScreen) {
        adopt(chat);
        open(chat.id);
      }
    },
    [adopt, chatId, list, open, projectId, taskId],
  );
}
